import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, database } from '../../firebaseConfig.js';
import { doc, getDoc, collection, query, orderBy, onSnapshot, updateDoc } from 'firebase/firestore';
import { FiBell, FiUser, FiLogOut, FiPlay, FiBookOpen, FiAward, FiClock, FiTrendingUp, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { FaGraduationCap, FaChartLine, FaClipboardList, FaUserEdit, FaQuestionCircle } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

// Components
import Login from '../Main/login.js';
import Sidebar from '../Sidebar/index.js';
import Header from '../Header/index.js';
import NotificationPanel from '../Notification Panel/index.js';
import ProfileForm from '../Profile Form';
import SEO from '../SEO';
import { useTheme } from '../../context/ThemeContext';

// Typewriter Tutorial Component
const TypewriterTutorial = ({ onComplete }) => {
  const { isDark } = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  const tutorialSteps = [
    { title: "Welcome to Prepify! 🎉", text: "Your ultimate preparation platform for NUST Entry Test (NET) and other competitive exams." },
    { title: "📚 How to Get Started", text: "Click 'Start Quiz' to begin a mock test based on your selected field of study. Questions are tailored to your preparation needs." },
    { title: "📊 Track Your Progress", text: "View your stats here - Total Tests, Overall Grade, and Questions Attempted. Watch your performance improve over time!" },
    { title: "🎯 Make Your Own Test", text: "Create custom quizzes by selecting categories, difficulty level, and number of questions. Perfect for focused practice!" },
    { title: "⏱️ Recent Activity", text: "Your test history appears below. Review past attempts, scores, and identify areas for improvement." },
    { title: "🚀 Ready to Begin!", text: "That's all! Start your first quiz and begin your journey to success. Good luck!" }
  ];

  useEffect(() => {
    if (currentStep >= tutorialSteps.length) {
      onComplete();
      return;
    }

    const fullText = tutorialSteps[currentStep].text;
    let charIndex = 0;
    setDisplayedText('');
    setIsTyping(true);

    const typeInterval = setInterval(() => {
      if (charIndex < fullText.length) {
        setDisplayedText(fullText.substring(0, charIndex + 1));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
      }
    }, 30);

    return () => clearInterval(typeInterval);
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  return (
    <motion.div
      className={`rounded-2xl p-6 mb-8 relative overflow-hidden ${isDark
        ? 'bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/20'
        : 'bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200'
        }`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Progress dots */}
      <div className="flex gap-2 mb-4">
        {tutorialSteps.map((_, idx) => (
          <div
            key={idx}
            className={`h-1.5 rounded-full transition-all ${idx === currentStep
              ? 'w-8 bg-gradient-to-r from-purple-500 to-pink-500'
              : idx < currentStep
                ? 'w-4 bg-purple-400'
                : 'w-4 bg-gray-400/30'
              }`}
          />
        ))}
      </div>

      <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        {tutorialSteps[currentStep]?.title}
      </h3>

      <p className={`text-base mb-6 min-h-[60px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
        {displayedText}
        {isTyping && <span className="inline-block w-0.5 h-5 bg-purple-500 ml-1 animate-pulse" />}
      </p>

      <div className="flex items-center justify-between">
        <button
          onClick={handleSkip}
          className={`text-sm font-medium transition-colors ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
            }`}
        >
          Skip Tutorial
        </button>
        <motion.button
          onClick={handleNext}
          disabled={isTyping}
          className={`px-5 py-2 rounded-lg font-medium text-white transition-all ${isTyping
            ? 'bg-gray-500 cursor-not-allowed'
            : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:shadow-lg'
            }`}
          whileHover={!isTyping ? { scale: 1.05 } : {}}
          whileTap={!isTyping ? { scale: 0.95 } : {}}
        >
          {currentStep === tutorialSteps.length - 1 ? "Let's Start!" : 'Next'}
        </motion.button>
      </div>
    </motion.div>
  );
};

// Calculate grade from score
const calculateGrade = (score) => {
  if (score >= 90) return 'A+';
  if (score >= 85) return 'A';
  if (score >= 80) return 'A-';
  if (score >= 75) return 'B+';
  if (score >= 70) return 'B';
  if (score >= 65) return 'B-';
  if (score >= 60) return 'C+';
  if (score >= 55) return 'C';
  if (score >= 50) return 'C-';
  if (score >= 45) return 'D+';
  if (score >= 40) return 'D';
  return 'F';
};

// Calculate overall grade from all tests (lenient algorithm)
const calculateOverallGrade = (quizHistory) => {
  if (!quizHistory || quizHistory.length === 0) return 'N/A';

  // Lenient algorithm: Use weighted average with recent tests weighted more
  const weights = quizHistory.map((_, idx) => 1 + (idx * 0.2)); // More recent = higher weight
  const totalWeight = weights.reduce((a, b) => a + b, 0);

  const weightedSum = quizHistory.reduce((acc, quiz, idx) => {
    return acc + ((quiz.score || 0) * weights[idx]);
  }, 0);

  const averageScore = weightedSum / totalWeight;

  // Apply lenient curve: add 5% bonus for consistent attempts
  const bonus = Math.min(5, quizHistory.length * 0.5);
  const finalScore = Math.min(100, averageScore + bonus);

  return calculateGrade(finalScore);
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [user, setUser] = useState(null);
  const [userName, setUserName] = useState('');
  const [isProfileIncomplete, setIsProfileIncomplete] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [startError, setStartError] = useState(null);
  const [isStartingMockTest, setIsStartingMockTest] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(false);

  // Stats state
  const [stats, setStats] = useState({
    totalTests: 0,
    overallGrade: 'N/A',
    totalQuestionsAttempted: 0
  });

  // Recent activity state
  const [recentActivity, setRecentActivity] = useState([]);

  // 1. Auth Listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const userRef = doc(database, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            setIsProfileIncomplete(!userData.profileCompleted);

            // Check if first visit (show tutorial)
            if (!userData.tutorialCompleted) {
              setShowTutorial(true);
              setIsFirstVisit(true);
            }

            // Fetch user name
            const name = userData?.personalInfo?.name || currentUser.displayName || currentUser.email?.split('@')[0] || 'User';
            setUserName(name);

            // Fetch quiz stats and history
            const quizStats = userData?.quizStats || {
              totalTests: 0,
              totalQuestionsAttempted: 0,
              totalCorrectAnswers: 0
            };
            const quizHistory = userData?.quizHistory || [];

            // Use stored overall grade or calculate from history
            const overallGrade = userData?.overallGrade || calculateOverallGrade(quizHistory);

            setStats({
              totalTests: quizStats.totalTests || quizHistory.length,
              overallGrade: overallGrade,
              totalQuestionsAttempted: quizStats.totalQuestionsAttempted || quizHistory.reduce((acc, quiz) => acc + (quiz.totalQuestions || 0), 0)
            });

            // Set recent activity from quiz history (most recent 5 for preview)
            const recent = quizHistory.slice(-5).reverse().map((quiz, index) => ({
              id: quiz.id || index,
              title: quiz.testName || quiz.mockTestName || quiz.category || `Quiz Session`,
              testType: quiz.testType || 'quiz',
              status: (quiz.score || 0) >= 50 ? 'completed' : 'failed',
              time: quiz.completedAt ? new Date(quiz.completedAt).toLocaleDateString() : 'Recently',
              score: quiz.score || 0,
              questionsAttempted: quiz.totalQuestions || 0,
              correctAnswers: quiz.correctAnswers || 0,
              questionsPreview: quiz.questionsPreview || []
            }));

            setRecentActivity(recent);
          } else {
            // New user - show tutorial
            setShowTutorial(true);
            setIsFirstVisit(true);
            const name = currentUser.displayName || currentUser.email?.split('@')[0] || 'User';
            setUserName(name);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        setUserName('');
      }
    });
    return () => unsubscribe();
  }, []);

  // 2. Notifications Listener
  useEffect(() => {
    const notificationsRef = collection(database, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      const newNotifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setNotifications(newNotifications);
    });

    return () => unsubscribe();
  }, []);

  const handleTutorialComplete = async () => {
    setShowTutorial(false);
    if (user && isFirstVisit) {
      try {
        const userRef = doc(database, 'users', user.uid);
        await updateDoc(userRef, { tutorialCompleted: true });
      } catch (error) {
        console.error('Error updating tutorial status:', error);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      navigate('/');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const handleStartMockTest = async () => {
    if (!user) {
      setStartError('Please log in to start a mock test.');
      return;
    }

    setStartError(null);
    setIsStartingMockTest(true);

    try {
      const userRef = doc(database, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        setStartError('Please complete your profile before starting a mock test.');
        setIsStartingMockTest(false);
        return;
      }

      const userData = userDoc.data();
      const preparingFor = userData.personalInfo?.preparingFor;

      if (!preparingFor) {
        setStartError('Please select your field of study in your profile before starting a mock test.');
        setIsStartingMockTest(false);
        return;
      }

      navigate('/mock-test');
    } catch (err) {
      console.error('Failed to start mock test:', err);
      setStartError('Could not start your mock test. Please try again.');
    } finally {
      setIsStartingMockTest(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen">
        <Login
          onLoginSuccess={(currentUser) => setUser(currentUser)}
          setError={() => { }}
          isProfileIncomplete={isProfileIncomplete}
          setIsProfileIncomplete={setIsProfileIncomplete}
        />
      </div>
    );
  }

  const StatCard = ({ title, value, subtitle, icon: Icon, gradient }) => (
    <motion.div
      className={`relative overflow-hidden rounded-2xl p-6 ${isDark
        ? 'bg-dark-secondary/50 border border-white/5'
        : 'bg-white border border-gray-200 shadow-lg'
        }`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02, y: -5 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {title}
          </p>
          <h3 className={`text-3xl font-bold mt-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {value}
          </h3>
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {subtitle}
          </p>
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient}`}>
          <Icon className="text-white text-xl" />
        </div>
      </div>
      {/* Decorative gradient line */}
      <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${gradient}`} />
    </motion.div>
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-500 bg-green-500/10';
      case 'pending': return 'text-yellow-500 bg-yellow-500/10';
      case 'failed': return 'text-red-500 bg-red-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return FiCheckCircle;
      case 'failed': return FiXCircle;
      default: return FiClock;
    }
  };

  return (
    <>
      <SEO
        title="Prepify - #1 NUST Entry Test (NET) Preparation Platform | Home"
        description="Start your NUST Entry Test preparation journey with Prepify. Access comprehensive mock tests, past papers, and practice questions for NET Engineering, CS, and Business."
        keywords="NUST Entry Test, NET Preparation, NUST Mock Test, NET Engineering, NET Computer Science, NET Business, NUST Past Papers"
        url="/"
      />

      {/* Main Layout Wrapper */}
      <div className={`flex flex-col md:flex-row min-h-screen transition-colors duration-300 ${isDark
        ? 'bg-dark-primary'
        : 'bg-gradient-to-br from-gray-50 via-white to-gray-100'
        }`}>

        {/* Sidebar */}
        <Sidebar />

        {/* Content Area */}
        <div className="flex-1 flex flex-col min-h-screen">
          {/* Header */}
          <Header
            userName={userName}
            onNotificationClick={() => setShowNotifications(!showNotifications)}
            notificationCount={notifications.length}
          />

          {/* Notification Panel - Top Right Dropdown */}
          <NotificationPanel
            notifications={notifications}
            onClose={() => setShowNotifications(false)}
            isOpen={showNotifications}
          />

          {/* Profile Form Overlay */}
          {isProfileIncomplete && (
            <ProfileForm user={user} setIsProfileIncomplete={setIsProfileIncomplete} />
          )}

          {/* Main Dashboard Content */}
          <main className="flex-1 p-4 md:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">

              {/* Tutorial for First-Time Users */}
              <AnimatePresence>
                {showTutorial && (
                  <TypewriterTutorial onComplete={handleTutorialComplete} />
                )}
              </AnimatePresence>

              {/* Welcome Section */}
              <motion.section
                className="mb-8"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className={`text-2xl md:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                      Welcome, {userName}!
                      <span className="ml-2 inline-block animate-bounce">👋</span>
                    </h1>
                    <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Ready to ace your preparation today?
                    </p>
                  </div>
                  <div className="flex gap-3">
                    {!showTutorial && (
                      <motion.button
                        onClick={() => setShowTutorial(true)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${isDark
                          ? 'bg-white/5 text-gray-300 hover:bg-white/10'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <FaQuestionCircle />
                        Help
                      </motion.button>
                    )}
                    <motion.button
                      onClick={handleStartMockTest}
                      disabled={isStartingMockTest}
                      className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${isStartingMockTest
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-600 via-violet-600 to-pink-600 hover:shadow-lg hover:shadow-purple-500/25 hover:scale-105'
                        } text-white`}
                      whileHover={!isStartingMockTest ? { scale: 1.05 } : {}}
                      whileTap={!isStartingMockTest ? { scale: 0.95 } : {}}
                    >
                      <FiPlay className="text-lg" />
                      {isStartingMockTest ? 'Starting...' : 'Start Quiz'}
                    </motion.button>
                  </div>
                </div>
                {startError && (
                  <p className="mt-2 text-sm text-red-500 font-medium">{startError}</p>
                )}
              </motion.section>

              {/* Stats Grid - Updated with requested stats */}
              <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
                <StatCard
                  title="Total Tests"
                  value={stats.totalTests}
                  subtitle="Tests attempted"
                  icon={FaChartLine}
                  gradient="from-blue-500 to-cyan-500"
                />
                <StatCard
                  title="Overall Grade"
                  value={stats.overallGrade}
                  subtitle="Based on all scores"
                  icon={FiAward}
                  gradient="from-purple-500 to-pink-500"
                />
                <StatCard
                  title="Questions Attempted"
                  value={stats.totalQuestionsAttempted}
                  subtitle="Total questions"
                  icon={FiTrendingUp}
                  gradient="from-green-500 to-emerald-500"
                />
              </section>

              {/* Recent Tests Section */}
              <section className={`rounded-2xl overflow-hidden ${isDark
                ? 'bg-dark-secondary/50 border border-white/5'
                : 'bg-white border border-gray-200 shadow-lg'
                }`}>
                <div className={`px-6 py-4 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                  <div className="flex items-center justify-between">
                    <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Recent Tests
                    </h2>
                    <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      Your quiz history
                    </span>
                  </div>
                </div>

                {recentActivity.length === 0 ? (
                  <div className={`px-6 py-12 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <FaClipboardList className="mx-auto text-4xl mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">No tests attempted yet</p>
                    <p className="text-sm">Start your first quiz to see your history here!</p>
                  </div>
                ) : (
                  <>
                    {/* Table Header */}
                    <div className={`px-6 py-3 grid grid-cols-12 gap-4 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-500 bg-dark-tertiary/30' : 'text-gray-500 bg-gray-50'
                      }`}>
                      <div className="col-span-1">#</div>
                      <div className="col-span-3">Test Name</div>
                      <div className="col-span-2">Status</div>
                      <div className="col-span-2">Date</div>
                      <div className="col-span-2">Questions</div>
                      <div className="col-span-2 text-right">Score</div>
                    </div>

                    {/* Table Body */}
                    <div className="divide-y divide-white/5">
                      {recentActivity.map((item, index) => {
                        const StatusIcon = getStatusIcon(item.status);
                        return (
                          <motion.div
                            key={item.id}
                            className={`px-6 py-4 grid grid-cols-12 gap-4 items-center transition-colors ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'
                              }`}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <div className="col-span-1">
                              <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium ${item.status === 'completed' ? 'bg-green-500/10 text-green-500' :
                                item.status === 'failed' ? 'bg-red-500/10 text-red-500' :
                                  'bg-yellow-500/10 text-yellow-500'
                                }`}>
                                {index + 1}
                              </span>
                            </div>
                            <div className="col-span-3">
                              <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {item.title}
                              </p>
                            </div>
                            <div className="col-span-2">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                                <StatusIcon className="text-xs" />
                                {item.status === 'completed' ? 'Passed' : 'Failed'}
                              </span>
                            </div>
                            <div className="col-span-2">
                              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                {item.time}
                              </p>
                            </div>
                            <div className="col-span-2">
                              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                {item.correctAnswers}/{item.questionsAttempted}
                              </p>
                            </div>
                            <div className="col-span-2 text-right">
                              <span className={`text-sm font-semibold ${item.score >= 70 ? 'text-green-500' :
                                item.score >= 50 ? 'text-yellow-500' :
                                  'text-red-500'
                                }`}>
                                {item.score}%
                              </span>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </>
                )}
              </section>

              {/* Quick Actions */}
              <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
                {[
                  { label: 'Make Your Own Test', icon: FaClipboardList, path: '/make-your-own-test', gradient: 'from-purple-500 to-pink-500' },
                  { label: 'Attempted Questions', icon: FiBookOpen, path: '/attempted-questions', gradient: 'from-blue-500 to-cyan-500' },
                  { label: 'View Profile', icon: FaUserEdit, path: '/profile', gradient: 'from-green-500 to-emerald-500' },
                  { label: 'Logout', icon: FiLogOut, path: null, gradient: 'from-red-500 to-orange-500', action: handleLogout },
                ].map((item, index) => (
                  <motion.button
                    key={item.label}
                    onClick={() => item.action ? item.action() : navigate(item.path)}
                    className={`flex items-center gap-3 p-4 rounded-xl transition-all ${isDark
                      ? 'bg-dark-secondary/50 border border-white/5 hover:border-white/10'
                      : 'bg-white border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md'
                      }`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className={`p-2.5 rounded-lg bg-gradient-to-br ${item.gradient}`}>
                      <item.icon className="text-white text-lg" />
                    </div>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {item.label}
                    </span>
                  </motion.button>
                ))}
              </section>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default LandingPage;